import { useTranslation } from 'react-i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { useClusterCategories, useClusters } from '@/api/generated';
import { ClusterCard } from '@/components/blocks/clusters/cluster-card';
import { ClusterCategoryFilter } from '@/components/blocks/clusters/cluster-category-filter';
import { ClusterProgress } from '@/components/blocks/clusters/cluster-progress';
import PageLayout from '@/components/blocks/layout/page-layout';
import { ShelvesSkeleton } from '@/components/blocks/shelves/shelves-skeleton';
import { Button } from '@/components/ui/button';
import { CommonError } from '@/components/ui/common-error';

const route = getRouteApi('/app/clusters');

const Clusters = () => {
  const { t } = useTranslation();
  const navigate = route.useNavigate();
  // Filters live in the URL so coming back from a review keeps them.
  const { category = [], showFinished = false, page = 0 } = route.useSearch();
  const { data, isLoading, isError } = useClusters(
    { category: category.length ? category : undefined, showFinished, page },
    // Refreshed so clusters others open or leave show their lock promptly.
    { query: { placeholderData: keepPreviousData, refetchInterval: 30_000 } }
  );
  const { data: categories } = useClusterCategories();

  const setFilters = async (next: {
    category?: Array<string>;
    showFinished?: boolean;
  }) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }) });
  const setPage = async (next: number) =>
    navigate({ search: (prev) => ({ ...prev, page: next || undefined }) });

  if (isLoading) {
    return <ShelvesSkeleton />;
  }

  if (isError || !data) {
    return <CommonError />;
  }

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  // Clusters still waiting outside the chosen categories.
  const elsewhere = category.length
    ? (categories?.items ?? [])
        .filter((item) => !category.includes(item.name))
        .reduce((sum, item) => sum + item.remaining, 0)
    : 0;
  const from = page * data.pageSize + 1;

  return (
    <PageLayout>
      <div className='page-top-container justify-start'>
        <span className='page-top-title'>{t('clusters.listTitle')}</span>
        <span
          className='text-muted-foreground -ml-2 truncate text-sm'
          aria-hidden='true'>
          ·
        </span>
        <span className='text-muted-foreground -ml-2 truncate text-sm'>
          {t('clusters.hint')}
        </span>
      </div>

      <ClusterProgress
        className='px-4'
        progress={data.progress}
        categories={category}
      />

      <div className='flex flex-wrap items-center gap-3 px-4'>
        <ClusterCategoryFilter
          categories={categories?.items ?? []}
          value={category}
          onValueChange={async (value) =>
            setFilters({ category: value.length ? value : undefined })
          }
        />
        <Button
          data-testid='clusters-toggle-finished'
          variant={showFinished ? 'default' : 'outline'}
          size='sm'
          aria-pressed={showFinished}
          onClick={async () =>
            setFilters({ showFinished: showFinished ? undefined : true })
          }>
          {showFinished
            ? t('clusters.hideFinished')
            : t('clusters.showFinished')}
        </Button>
        {data.total > 0 && (
          <div className='ml-auto flex items-center gap-2'>
            <span className='text-muted-foreground text-sm'>
              {t('clusters.range', {
                from,
                to: from + data.items.length - 1,
                total: data.total,
              })}
            </span>
            <Button
              data-testid='clusters-prev-page'
              variant='outline'
              size='sm'
              disabled={page === 0}
              onClick={async () => setPage(page - 1)}>
              {t('clusters.previous')}
            </Button>
            <Button
              data-testid='clusters-next-page'
              variant='outline'
              size='sm'
              disabled={page >= pages - 1}
              onClick={async () => setPage(page + 1)}>
              {t('clusters.next')}
            </Button>
          </div>
        )}
      </div>

      {data.items.length === 0 ? (
        <div className='text-muted-foreground mx-4 flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-sm'>
          {category.length
            ? t('clusters.emptyInCategories')
            : t('clusters.empty')}
          <div className='flex flex-wrap justify-center gap-2'>
            {elsewhere > 0 && (
              <Button
                data-testid='clusters-show-unfinished'
                size='sm'
                onClick={async () => setFilters({ category: undefined })}>
                {t('clusters.showUnfinished', { count: elsewhere })}
              </Button>
            )}
            {!showFinished && (
              <Button
                data-testid='clusters-show-finished'
                variant='outline'
                size='sm'
                onClick={async () => setFilters({ showFinished: true })}>
                {t('clusters.showFinished')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className='grid grid-cols-2 gap-4 px-4 pt-2 pb-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'>
          {data.items.map((cluster) => (
            <ClusterCard
              key={cluster.id}
              cluster={cluster}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default Clusters;
